<script>
  import { format } from 'svelte-i18n';
  import { useNavigate } from 'svelte-navigator';
  import { Button, Modal, ModalBody, ModalFooter } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { getNotificationsContext } from '@client/components/notifications';
  import { Spinner } from '@client/components/spinner';

  import { GroupsService } from '@client/services/groups';

  import { appStore } from '@client/stores/app';

  import { GROUP_DEFAULTS } from '@client/enums';

  import { deepCopy } from '@common/utils/objects';

  import { GroupFormContent } from '../shared';

  const { addNotification } = getNotificationsContext();
  const navigate = useNavigate();

  export let show = false;
  export let updating = false;

  const defaultOutlookAddin = {
    disableRemoveDefaultSignature: false,
    disableAutoInsertSignatures: false,
    enableVisualPreview: false,
    disablePlainTextPreview: false,
    defaultSignatureMode: 'preview',
    mergeParams: false,
    ignoreblankmergefields: false,
    enableOverrides: false
  };

  let group = deepCopy(GROUP_DEFAULTS);
  let invalid = false;
  let gmailpush = { automatic: false, pushtype: 'ptp' };
  let outlookaddin = { ...defaultOutlookAddin };
  let helpers = [];

  const notify = (text, error) => {
    addNotification({
      text,
      type: error ? 'error' : 'success',
      autohide: 2500,
      position: 'top-right'
    });
  };

  const resetState = () => {
    group = deepCopy(GROUP_DEFAULTS);
    gmailpush = { automatic: false, pushtype: 'ptp' };
    outlookaddin = { ...defaultOutlookAddin };
    helpers = [];
  };

  const onCreate = async () => {
    $appStore.loading = true;

    const payload = {
      ...group,
      uihelpers: helpers,
      outlookaddin: { ...outlookaddin },
      gmailpush: {
        automatic: gmailpush.automatic,
        pushtype: gmailpush.pushtype?.value || gmailpush.pushtype || 'ptp'
      }
    };

    try {
      const response = await GroupsService.createGroup(payload);

      if (response.status === 400) {
        notify($format('label.FAILURE.CREATE_GROUP', { values: { item: response.message } }), true);
      } else {
        notify($format('label.SUCCESS.CREATE_GROUP', { values: { item: response.groupname } }));
        resetState();
        show = false;
        navigate(`/manage/groups/${response.groupname}`);
      }
    } finally {
      $appStore.loading = false;
    }
  };

  const onModalClose = () => {
    show = false;
    resetState();
  };

  $: isDisabled = !group.groupname || invalid;
</script>

<Modal bind:isOpen={show} size="lg" fullscreen="lg" scrollable>
  <ModalBody class="create-group-modal">
    <button class="close" on:click={onModalClose}>
      <Icon name="x" />
    </button>

    <div>
      <div class="title">
        <h1>{$format('label.NEW_GROUP')}</h1>
        <p>{$format('label.CREATE_GROUP_HEADER')}</p>
      </div>

      <GroupFormContent bind:group bind:invalid bind:gmailpush bind:outlookaddin bind:helpers />
    </div>
  </ModalBody>
  <ModalFooter>
    <Button color="primary" disabled={isDisabled} on:click={onCreate}>
      {#if updating}
        <Spinner show inline />
      {:else}
        {$format('label.CREATE_GROUP')}
      {/if}
    </Button>
  </ModalFooter>
</Modal>

<style lang="scss" src="./create.scss"></style>
